/**
 * WebSocket Service for Real-time Ride Tracking
 * Handles connection, reconnection, and message handling
 */

class WebSocketService {
  constructor() {
    this.ws = null;
    this.userId = null;
    this.messageHandlers = new Map();
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectDelay = 1000;
    this.isConnected = false;
    this.pendingMessages = [];
  }

  /**
   * Connect to WebSocket server
   * @param {string} userId - User ID to identify the connection
   */
  connect(userId) {
    if (this.ws && this.isConnected && this.userId === userId) {
      return;
    }

    this.userId = userId;
    
    // Check if running in Capacitor native app
    let isNative = false;
    try {
      const { Capacitor } = require('@capacitor/core');
      isNative = Capacitor.isNativePlatform();
    } catch (e) {
      // Capacitor not available
    }
    
    // Get WebSocket URL - handle Capacitor native app
    let apiUrl = process.env.REACT_APP_BACKEND_URL || '';
    
    // If native Capacitor app, use production URL
    if (isNative) {
      apiUrl = 'https://bizilgo.com';
    } else if (!apiUrl && typeof window !== 'undefined') {
      const protocol = window.location.protocol;
      if (protocol === 'capacitor:' || protocol === 'file:' || protocol === 'ionic:') {
        apiUrl = 'https://bizilgo.com';
      } else if (protocol === 'https:' || protocol === 'http:') {
        apiUrl = window.location.origin;
      }
    }
    
    // Final fallback
    if (!apiUrl) {
      apiUrl = 'https://bizilgo.com';
    }
    
    const wsProtocol = apiUrl.startsWith('https') ? 'wss' : 'ws';
    const wsHost = apiUrl.replace(/^https?:\/\//, '').replace(/\/api$/, '');
    const wsUrl = `${wsProtocol}://${wsHost}/ws/${userId}`;
    
    console.log('[WebSocket] Connecting to:', wsUrl);

    try {
      this.ws = new WebSocket(wsUrl);

      this.ws.onopen = () => {
        this.isConnected = true;
        this.reconnectAttempts = 0;
        
        // Send any pending messages
        while (this.pendingMessages.length > 0) {
          const msg = this.pendingMessages.shift();
          this.send(msg);
        }

        // Notify handlers
        this.triggerHandler('connected', { userId });
      };

      this.ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          
          // Trigger specific handler
          if (data.type) {
            this.triggerHandler(data.type, data);
          }
          
          // Also trigger general message handler
          this.triggerHandler('message', data);
        } catch (e) {
          console.error('Error parsing WebSocket message:', e);
        }
      };

      this.ws.onclose = (event) => {
        this.isConnected = false;
        this.triggerHandler('disconnected', { code: event.code, reason: event.reason });
        
        // Attempt reconnection
        if (this.reconnectAttempts < this.maxReconnectAttempts) {
          this.reconnectAttempts++;
          const delay = this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1);
          setTimeout(() => this.connect(userId), delay);
        }
      };

      this.ws.onerror = (error) => {
        console.error('WebSocket error:', error);
        this.triggerHandler('error', { error });
      };

    } catch (error) {
      console.error('Error creating WebSocket:', error);
    }
  }

  /**
   * Disconnect from WebSocket server
   */
  disconnect() {
    if (this.ws) {
      this.ws.close();
      this.ws = null;
      this.isConnected = false;
      this.userId = null;
    }
  }

  /**
   * Send a message through WebSocket
   * @param {object} data - Message data to send
   */
  send(data) {
    if (this.ws && this.isConnected) {
      this.ws.send(JSON.stringify(data));
    } else {
      // Queue message for later
      this.pendingMessages.push(data);
    }
  }

  /**
   * Subscribe to ride tracking updates
   * @param {string} rideId - Ride ID to track
   */
  subscribeToRide(rideId) {
    this.send({
      type: 'subscribe_ride',
      ride_id: rideId
    });
  }

  /**
   * Unsubscribe from ride tracking
   * @param {string} rideId - Ride ID to stop tracking
   */
  unsubscribeFromRide(rideId) {
    this.send({
      type: 'unsubscribe_ride',
      ride_id: rideId
    });
  }

  /**
   * Request current driver location
   * @param {string} rideId - Ride ID
   */
  requestDriverLocation(rideId) {
    this.send({
      type: 'get_driver_location',
      ride_id: rideId
    });
  }

  /**
   * Send driver location update (for driver app)
   * @param {number} lat - Latitude
   * @param {number} lng - Longitude
   * @param {string} vehicleType - Vehicle type
   * @param {number} heading - Direction heading (optional)
   */
  sendDriverLocation(lat, lng, vehicleType, heading = null) {
    this.send({
      type: 'driver_location',
      lat,
      lng,
      vehicle_type: vehicleType,
      heading,
      timestamp: new Date().toISOString()
    });
  }

  /**
   * Set driver online status
   * @param {boolean} isOnline - Online status
   * @param {number} lat - Current latitude
   * @param {number} lng - Current longitude
   * @param {string} vehicleType - Vehicle type
   */
  setDriverOnline(isOnline, lat, lng, vehicleType) {
    this.send({
      type: isOnline ? 'driver_online' : 'driver_offline',
      lat,
      lng,
      vehicle_type: vehicleType
    });
  }

  /**
   * Send ping to keep connection alive
   */
  ping() {
    this.send({ type: 'ping' });
  }

  /**
   * Add a message handler
   * @param {string} eventType - Event type to handle
   * @param {function} handler - Handler function
   * @returns {function} - Unsubscribe function
   */
  on(eventType, handler) {
    if (!this.messageHandlers.has(eventType)) {
      this.messageHandlers.set(eventType, new Set());
    }
    this.messageHandlers.get(eventType).add(handler);

    // Return unsubscribe function
    return () => {
      const handlers = this.messageHandlers.get(eventType);
      if (handlers) {
        handlers.delete(handler);
      }
    };
  }

  /**
   * Remove a message handler
   * @param {string} eventType - Event type
   * @param {function} handler - Handler function to remove
   */
  off(eventType, handler) {
    const handlers = this.messageHandlers.get(eventType);
    if (handlers) {
      handlers.delete(handler);
    }
  }

  /**
   * Trigger handlers for an event
   * @param {string} eventType - Event type
   * @param {object} data - Event data
   */
  triggerHandler(eventType, data) {
    const handlers = this.messageHandlers.get(eventType);
    if (handlers) {
      handlers.forEach(handler => {
        try {
          handler(data);
        } catch (e) {
          console.error(`Error in ${eventType} handler:`, e);
        }
      });
    }
  }

  /**
   * Check if connected
   * @returns {boolean}
   */
  getConnectionStatus() {
    return this.isConnected;
  }
}

// Export singleton instance
const wsService = new WebSocketService();
export default wsService;
